/*

   Problem: you have a bit of state that you would store in useState,
   but you want the chosen value to be "remembered" for when the user
   comes back. Say, a "Select Organisation" select box.

   Solution: use this hook, which is a wrapper around useState that also
   persists the value to localStorage or sessionStorage. And also syncs
   the value across multiple calls of this hook with the same key.

   Based on https://usehooks-ts.com/react-hook/use-local-storage
 */

import { isEqual } from "lodash";
import { useCallback, useState } from "react";

import { useEventListener } from "./use-event-listener";

type StorageReturn<T> = [T, (t: T | ((prev: T) => T)) => void];

function isFunction<T>(t: T | (() => T)): t is () => T {
  // If we use this check directly in the code below, Typescript complains
  // that it may still be the case that t is of type T, which just happens
  // the be a function, and doesn't know how to call it. But since we know
  // only JSON serializable values are allowed for T, we can use this type guard
  // to force the type to () => T if this function returns true.
  return typeof t === "function";
}

type Storage = typeof window.localStorage | typeof window.sessionStorage;

const CUSTOM_EVENT_KEY = "custom-storage";

function useStorage<T>(
  storage: Storage,
  key: string,
  initialValue: T | (() => T)
): StorageReturn<T> {
  const readValue = useCallback((): T => {
    try {
      // Get from storage by key
      const item = storage.getItem(key);
      // Parse stored json or if none return initialValue
      if (item) {
        return JSON.parse(item) as T;
      } else if (isFunction(initialValue)) {
        return initialValue();
      } else {
        return initialValue;
      }
    } catch (error) {
      // If error also return initialValue
      return isFunction(initialValue) ? initialValue() : initialValue;
    }
  }, [initialValue, key, storage]);

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(readValue);

  // Return a wrapped version of useState's setter function that
  // persists the new value to the storage.
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        setStoredValue((prev) => {
          const valueToStore = value instanceof Function ? value(prev) : value;
          // If the value is the same as the previous value, don't update the storage or dispatch an event
          if (isEqual(prev, valueToStore)) {
            return prev;
          }
          // Save to local storage
          storage.setItem(key, JSON.stringify(valueToStore));
          // We dispatch a custom event so every similar useStorage hook is notified
          window.dispatchEvent(new StorageEvent(CUSTOM_EVENT_KEY, { key }));
          // This only happens if non-JSON-able values are used, don't do that.
          return valueToStore;
        });
      } catch (error) {
        console.log("Exception in useStorage setValue with value:", value, "error:", error);
      }
    },
    [key, storage]
  );

  const handleStorageChange = useCallback(
    (event: StorageEvent | CustomEvent) => {
      if ((event as StorageEvent).key && (event as StorageEvent).key !== key) {
        return;
      }
      setStoredValue(readValue());
    },
    [key, readValue]
  );

  // this only works for other documents, not the current one
  useEventListener("storage", handleStorageChange);

  // this is a custom event, triggered in setValue
  useEventListener(CUSTOM_EVENT_KEY, handleStorageChange);

  return [storedValue, setValue];
}

export function useLocalStorage<T>(key: string, initialValue: T | (() => T)): StorageReturn<T> {
  return useStorage<T>(window.localStorage, key, initialValue);
}

export function useSessionStorage<T>(key: string, initialValue: T | (() => T)): StorageReturn<T> {
  return useStorage<T>(window.sessionStorage, key, initialValue);
}
